/**
 * Emissions Business Metrics Page
 *
 * Manage business metrics (employee count, revenue, floor area) for carbon intensity calculations.
 * Supports multiple periods to track changes over time.
 */

import { useState, type ReactNode } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Info, Users, DollarSign, Building2, Loader2, Pencil, Trash2, PieChart, Save } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Input } from '@g17eco/ui-modern/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@g17eco/ui-modern/components/ui/select';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@g17eco/ui-modern/components/ui/dialog';
import { generateUrl } from '@routes/util';
import { getCurrencySymbol } from '../../../utils/units';
import { emissionsTrackerRoutes } from '../routes';
import {
  useGetBusinessMetricsListQuery,
  useUpdateBusinessMetricsMutation,
  useDeleteBusinessMetricsMutation,
} from '../api/emissions';
import {
  BusinessMetrics,
  BusinessMetricsRequest,
  FloorAreaUnit,
  EmployeeCountType,
} from '../types/emissions';

const labelClass = 'tw:block tw:text-sm tw:font-medium tw:mb-1.5';

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'AED', 'AUD', 'SGD', 'JPY'].map((code) => {
  const symbol = getCurrencySymbol(code);
  return { value: code, label: symbol === code ? code : `${code} (${symbol})` };
});

interface MetricSectionProps {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}

const MetricSection = ({ icon, title, children }: MetricSectionProps) => (
  <div className='tw:border tw:border-border tw:rounded-lg tw:p-4'>
    <h5 className='tw:font-semibold tw:mb-3 tw:flex tw:items-center tw:gap-2 tw:text-sm'>
      {icon}
      {title}
    </h5>
    <div className='tw:grid tw:grid-cols-2 tw:gap-3'>{children}</div>
  </div>
);

interface NumberFieldProps {
  id: string;
  label: string;
  placeholder: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  hint?: string;
}

const NumberField = ({ id, label, placeholder, value, onChange, hint }: NumberFieldProps) => (
  <div>
    <label htmlFor={id} className={labelClass}>{label}</label>
    <Input
      id={id}
      type='number'
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
      placeholder={placeholder}
      min={0}
    />
    {hint ? <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>{hint}</p> : null}
  </div>
);

const getYear = (dateString: string): number => {
  return new Date(dateString).getFullYear();
};

const formatNumber = (value: number | undefined): string => {
  if (value === undefined) {
    return '--';
  }
  return value.toLocaleString();
};

const formatRevenue = (value: number | undefined, currency: string | undefined): string => {
  if (value === undefined) {
    return '--';
  }
  const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
  const millions = value / 1_000_000;
  if (millions >= 1) {
    return `${currencySymbol}${millions.toFixed(1)}M`;
  }
  const thousands = value / 1_000;
  if (thousands >= 1) {
    return `${currencySymbol}${thousands.toFixed(0)}K`;
  }
  return `${currencySymbol}${value.toLocaleString()}`;
};

const formatFloorArea = (value: number | undefined, unit: FloorAreaUnit | undefined): string => {
  if (value === undefined) {
    return '--';
  }
  const unitLabel = unit === FloorAreaUnit.SquareFeet ? 'ft²' : 'm²';
  return `${value.toLocaleString()} ${unitLabel}`;
};

interface MetricsFormProps {
  metrics?: BusinessMetrics | null;
  existingYears: number[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BusinessMetricsRequest) => Promise<void>;
  isSaving: boolean;
}

const MetricsForm = ({ metrics, existingYears, isOpen, onClose, onSave, isSaving }: MetricsFormProps) => {
  const isEdit = !!metrics;
  const currentYear = new Date().getFullYear();
  const editingYear = metrics?.periodStart ? getYear(metrics.periodStart) : null;

  const allYears = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);
  const availableYears = allYears.filter(
    (year) => !existingYears.includes(year) || year === editingYear
  );

  const getDefaultYear = () => {
    if (editingYear) {
      return editingYear;
    }
    if (availableYears.includes(currentYear)) {
      return currentYear;
    }
    return availableYears[availableYears.length - 1] ?? currentYear;
  };

  const getDefaultFormData = (): Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'> => ({
    employeeCount: metrics?.employeeCount,
    employeeCountType: metrics?.employeeCountType ?? EmployeeCountType.FTE,
    revenue: metrics?.revenue,
    revenueCurrency: metrics?.revenueCurrency ?? 'USD',
    floorArea: metrics?.floorArea,
    floorAreaUnit: metrics?.floorAreaUnit ?? FloorAreaUnit.SquareMeters,
    notes: metrics?.notes ?? '',
    dataSource: metrics?.dataSource ?? 'manual',
  });

  const [selectedYear, setSelectedYear] = useState<number>(getDefaultYear);
  const [formData, setFormData] = useState<Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'>>(getDefaultFormData);

  const handleSubmit = async () => {
    const fullData: BusinessMetricsRequest = {
      ...formData,
      periodStart: `${selectedYear}-01-01`,
      periodEnd: `${selectedYear}-12-31`,
    };
    await onSave(fullData);
  };

  const updateField = <K extends keyof Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'>>(
    field: K,
    value: Omit<BusinessMetricsRequest, 'periodStart' | 'periodEnd'>[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={isSaving ? undefined : onClose}>
      <DialogContent
        showCloseButton={!isSaving}
        aria-describedby={undefined}
        className='tw:sm:max-w-xl tw:max-h-[90vh] tw:flex tw:flex-col tw:gap-0 tw:p-0'
      >
        <DialogHeader className='tw:px-6 tw:py-4 tw:border-b tw:shrink-0'>
          <DialogTitle>{isEdit ? 'Edit Business Metrics' : 'Add Business Metrics'}</DialogTitle>
        </DialogHeader>

        <div className='tw:px-6 tw:py-4 tw:overflow-y-auto tw:space-y-4'>
          <div>
            <label htmlFor='metrics-year' className={labelClass}>Reporting Year</label>
            <Select value={String(selectedYear)} onValueChange={(value) => setSelectedYear(Number(value))}>
              <SelectTrigger id='metrics-year' className='tw:w-36'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='tw:text-xs tw:text-muted-foreground tw:mt-1'>Fiscal year (Jan 1 - Dec 31)</p>
          </div>

          <MetricSection icon={<Users className='tw:size-4 tw:text-primary' />} title='Employee Data'>
            <NumberField
              id='metrics-employee-count'
              label='Employee Count'
              placeholder='e.g., 150'
              value={formData.employeeCount}
              onChange={(value) => updateField('employeeCount', value)}
            />
            <div>
              <label htmlFor='metrics-count-type' className={labelClass}>Count Type</label>
              <Select
                value={formData.employeeCountType}
                onValueChange={(value) => updateField('employeeCountType', value as EmployeeCountType)}
              >
                <SelectTrigger id='metrics-count-type' className='tw:w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EmployeeCountType.FTE}>Full-Time Equivalent (FTE)</SelectItem>
                  <SelectItem value={EmployeeCountType.Headcount}>Headcount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </MetricSection>

          <MetricSection icon={<DollarSign className='tw:size-4 tw:text-emerald-600' />} title='Revenue Data'>
            <NumberField
              id='metrics-revenue'
              label='Annual Revenue'
              placeholder='e.g., 25000000'
              value={formData.revenue}
              onChange={(value) => updateField('revenue', value)}
              hint='Whole currency units (e.g., 25000000 for $25M)'
            />
            <div>
              <label htmlFor='metrics-currency' className={labelClass}>Currency</label>
              <Select value={formData.revenueCurrency} onValueChange={(value) => updateField('revenueCurrency', value)}>
                <SelectTrigger id='metrics-currency' className='tw:w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </MetricSection>

          <MetricSection icon={<Building2 className='tw:size-4 tw:text-blue-600' />} title='Floor Area Data'>
            <NumberField
              id='metrics-floor-area'
              label='Total Floor Area'
              placeholder='e.g., 5000'
              value={formData.floorArea}
              onChange={(value) => updateField('floorArea', value)}
            />
            <div>
              <label htmlFor='metrics-floor-unit' className={labelClass}>Unit</label>
              <Select
                value={formData.floorAreaUnit}
                onValueChange={(value) => updateField('floorAreaUnit', value as FloorAreaUnit)}
              >
                <SelectTrigger id='metrics-floor-unit' className='tw:w-full'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FloorAreaUnit.SquareMeters}>Square Meters (m²)</SelectItem>
                  <SelectItem value={FloorAreaUnit.SquareFeet}>Square Feet (ft²)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </MetricSection>

          <div>
            <label htmlFor='metrics-notes' className={labelClass}>Notes (Optional)</label>
            <textarea
              id='metrics-notes'
              value={formData.notes ?? ''}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder='Add any relevant notes about this data...'
              rows={2}
              className='tw:flex tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-2 tw:text-sm tw:shadow-xs tw:outline-none tw:placeholder:text-muted-foreground tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50 tw:resize-none'
            />
          </div>
        </div>

        <DialogFooter className='tw:px-6 tw:py-4 tw:border-t tw:shrink-0'>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
          >
            {isSaving ? (
              <>
                <Loader2 className='tw:size-4 tw:animate-spin tw:mr-2' />
                Saving...
              </>
            ) : (
              <>
                <Save className='tw:size-4' />
                {isEdit ? 'Save Changes' : 'Add Metrics'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EmissionsBusinessMetricsPage = () => {
  const history = useHistory();
  const { initiativeId } = useParams<{ initiativeId: string }>();

  const { data: metricsList, isLoading, error } = useGetBusinessMetricsListQuery({ initiativeId });
  const [updateMetrics, { isLoading: isSaving }] = useUpdateBusinessMetricsMutation();
  const [deleteMetrics] = useDeleteBusinessMetricsMutation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  // Bumped on every open so the form remounts with fresh state instead of keeping the last session's
  const [formKey, setFormKey] = useState(0);
  // Not cleared on close, so the closing dialog doesn't switch to "Add" during its fade-out
  const [editingMetrics, setEditingMetrics] = useState<BusinessMetrics | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ADMIN, { initiativeId }));
  };

  const handleOpenForm = (metrics?: BusinessMetrics) => {
    setEditingMetrics(metrics ?? null);
    setFormKey((key) => key + 1);
    setIsFormOpen(true);
  };

  const handleSave = async (data: BusinessMetricsRequest) => {
    await updateMetrics({ initiativeId, data });
    setIsFormOpen(false);
  };

  const handleDelete = async (metricsId: string) => {
    await deleteMetrics({ initiativeId, metricsId });
    setDeleteConfirmId(null);
  };

  return (
    <Dashboard>
      {/* Page Header */}
      <div className='tw:flex tw:items-center tw:justify-between tw:mb-6'>
        <div className='tw:flex tw:items-center tw:gap-3'>
          <Button variant='ghost' size='icon' onClick={handleBack} className='tw:text-muted-foreground'>
            <ArrowLeft className='tw:size-4' />
          </Button>
          <div>
            <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Business Metrics</h4>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
              Configure employee count, revenue, and floor area for carbon intensity calculations
            </p>
          </div>
        </div>
        <Button
          onClick={() => handleOpenForm()}
          className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
        >
          Add Metrics
        </Button>
      </div>

      {/* Info Card */}
      <div className='tw:flex tw:gap-3 tw:rounded-lg tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-4 tw:text-blue-800 tw:mb-6 tw:text-sm'>
        <Info className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
        <div>
          <strong>About Business Metrics</strong>
          <p className='tw:mb-0 tw:mt-0.5'>
            Business metrics are used to calculate carbon intensity ratios (emissions per employee,
            per revenue, per floor area). You can add metrics for different periods to track how your
            intensity changes over time.
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className='tw:flex tw:justify-center tw:py-12'>
          <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className='tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:text-red-800 tw:text-sm tw:mb-4'>
          Failed to load business metrics. Please try again.
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && (!metricsList || metricsList.length === 0) && (
        <Card>
          <CardContent className='tw:p-10 tw:text-center'>
            <div className='tw:flex tw:justify-center tw:mb-4'>
              <PieChart className='tw:size-12 tw:text-muted-foreground' />
            </div>
            <h5 className='tw:font-semibold tw:mb-2'>No Business Metrics Configured</h5>
            <p className='tw:text-sm tw:text-muted-foreground tw:mb-6'>
              Add your organization&apos;s business metrics to calculate carbon intensity ratios
              and track efficiency improvements over time.
            </p>
            <Button
              onClick={() => handleOpenForm()}
              className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
            >
              Add Your First Metrics
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Metrics List */}
      {!isLoading && !error && metricsList && metricsList.length > 0 && (
        <Card>
          <CardContent className='tw:p-0'>
            <div className='tw:overflow-x-auto'>
              <table className='tw:w-full tw:text-sm'>
                <thead className='tw:bg-muted/50'>
                  <tr>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Year</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Employees</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Revenue</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Floor Area</th>
                    <th className='tw:text-left tw:px-4 tw:py-3 tw:font-medium'>Source</th>
                    <th className='tw:text-right tw:px-4 tw:py-3 tw:font-medium'>Actions</th>
                  </tr>
                </thead>
                <tbody className='tw:divide-y'>
                  {metricsList.map((metrics) => (
                    <tr key={metrics._id} className='tw:hover:bg-muted/30'>
                      <td className='tw:px-4 tw:py-3'>
                        <span className='tw:font-medium'>{getYear(metrics.periodStart)}</span>
                        {metrics.notes && (
                          <span className='tw:block tw:text-xs tw:text-muted-foreground'>{metrics.notes}</span>
                        )}
                      </td>
                      <td className='tw:px-4 tw:py-3'>
                        <span>{formatNumber(metrics.employeeCount)}</span>
                        {metrics.employeeCountType && (
                          <span className='tw:block tw:text-xs tw:text-muted-foreground'>
                            {metrics.employeeCountType === EmployeeCountType.FTE ? 'FTE' : 'Headcount'}
                          </span>
                        )}
                      </td>
                      <td className='tw:px-4 tw:py-3'>{formatRevenue(metrics.revenue, metrics.revenueCurrency)}</td>
                      <td className='tw:px-4 tw:py-3'>{formatFloorArea(metrics.floorArea, metrics.floorAreaUnit)}</td>
                      <td className='tw:px-4 tw:py-3'>
                        {metrics.dataSource === 'sample' ? (
                          <Badge className='tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200'>Sample</Badge>
                        ) : (
                          <Badge className='tw:bg-emerald-500/10 tw:text-emerald-700 tw:border-emerald-200'>Manual</Badge>
                        )}
                      </td>
                      <td className='tw:px-4 tw:py-3 tw:text-right'>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => handleOpenForm(metrics)}
                          aria-label={`Edit ${getYear(metrics.periodStart)} metrics`}
                        >
                          <Pencil className='tw:size-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => setDeleteConfirmId(metrics._id)}
                          aria-label={`Delete ${getYear(metrics.periodStart)} metrics`}
                          className='tw:text-destructive tw:hover:text-destructive/80'
                        >
                          <Trash2 className='tw:size-4' />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <MetricsForm
        key={formKey}
        metrics={editingMetrics}
        existingYears={metricsList?.map((m) => getYear(m.periodStart)) ?? []}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className='tw:sm:max-w-sm'>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this business metrics record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}>
              <Trash2 className='tw:size-4' />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dashboard>
  );
};
